'use client';

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  resolveAnalyticsStack,
  storefrontCountry,
  type AnalyticsStack,
} from 'lib/analytics/runtime';
import { setConsentGate, markClickIdForwardingReady } from 'lib/consent/gate';
import {
  classifyRoute,
  decide,
  isGtmAllowed,
  releaseOptOut,
  type ConsentCategorySelection,
  type ConsentChoice,
  type ConsentDecision,
} from 'lib/consent/policy';
import {
  captureAdClickIds,
  expireTrackerStorage,
  forgetTrackerData,
  loadClarity,
  loadGoogleAds,
  loadGtm,
  loadHubSpot,
  loadMeta,
  loadMixpanel,
  loadReddit,
  loadTikTok,
  loadTripleWhale,
  loadWewomedia,
  loadGa4,
  needsFreshDocument,
} from 'lib/analytics/destinations';
import { trackerSkipsStorefront } from 'lib/analytics/trackers';
import {
  consentChoiceOf,
  readConsentRecord,
  writeConsentRecord,
} from './consentCookie';
import type { ConsentRecord } from 'lib/consent/record';
import {
  captureCampaignFromLocation,
  forgetCampaignClickIds,
  persistCampaign,
} from 'lib/analytics/campaign';
import {
  forgetLandingClickIds,
  persistLandingClickIds,
  rememberLandingParams,
  syncClickIdsInUrl,
} from 'lib/analytics/landingParams';
import { clearAnalyticsQueue } from 'lib/analytics/fanout';
import { PrivacyChoicesDialog } from './PrivacyChoicesDialog';
import {
  releaseTripleWhaleProductView,
  trackTripleWhalePageLoad,
  tripleWhaleProductViewCovers,
} from 'lib/analytics/tripleWhale';

export type GrantedCategories = {
  stats: boolean;
  analytics: boolean;
  ads: boolean;
  gtm: boolean;
  replay: boolean;
  attribution: boolean;
};

type ConsentRuntimeValue = {
  stack: AnalyticsStack;
  choice: ConsentChoice;
  consentKnown: boolean;
  gpc: boolean;
  granted: GrantedCategories;
  setBannerReady: (ready: boolean) => void;
  choose: (choice: 'accept' | 'reject') => void;
  openPrivacyChoices: () => void;
  saveCategories: (categories: ConsentCategorySelection) => void;
};

const CLOSED: GrantedCategories = {
  stats: false,
  analytics: false,
  ads: false,
  gtm: false,
  replay: false,
  attribution: false,
};

const ConsentRuntimeContext = createContext<ConsentRuntimeValue>({
  stack: 'next',
  choice: 'none',
  consentKnown: false,
  gpc: false,
  granted: CLOSED,
  setBannerReady: () => {},
  choose: () => {},
  openPrivacyChoices: () => {},
  saveCategories: () => {},
});

export function useConsentRuntime(): ConsentRuntimeValue {
  return useContext(ConsentRuntimeContext);
}

function readGpc(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

function grantCategories(
  stack: AnalyticsStack,
  known: boolean,
  decision: ConsentDecision,
  choice: ConsentChoice,
  bannerReady: boolean,
): GrantedCategories {
  if (stack !== 'next' || !known) return CLOSED;
  const stats = releaseOptOut(
    decision.modes.stats,
    decision.effective.stats,
    choice,
    bannerReady,
  );
  const analytics = releaseOptOut(
    decision.modes.analytics,
    decision.effective.analytics,
    choice,
    bannerReady,
  );
  const ads = releaseOptOut(
    decision.modes.ads,
    decision.effective.ads,
    choice,
    bannerReady,
  );
  return {
    stats,
    analytics,
    ads,
    replay: decision.effective.replay,
    gtm: isGtmAllowed({ analytics, ads }),
    attribution: decision.effective.attribution,
  };
}

export function ConsentRuntimeProvider({ children }: { children: ReactNode }) {
  const { store, region, detectedRegionFromGeoIP, geoRegion } =
    useRegionLocale();
  const pathname = usePathname() ?? '/';
  const [record, setRecord] = useState<ConsentRecord | null>(null);
  const [consentKnown, setConsentKnown] = useState(false);
  const [gpc, setGpc] = useState(false);
  const [bannerReady, setBannerReady] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setGpc(readGpc());
    setRecord(readConsentRecord());
    setConsentKnown(true);
    markClickIdForwardingReady();
  }, []);

  const choice: ConsentChoice = consentChoiceOf(record);

  const stack = resolveAnalyticsStack(store);
  const page = classifyRoute(
    pathname,
    typeof window === 'undefined' ? '' : window.location.search,
  );
  // The banner waits for state, so the first paint matches the server.
  // The gate does not. A stored choice is read here, before child effects,
  // or a mount event such as view_item is dropped and never retried.
  const liveRecord =
    consentKnown || typeof document === 'undefined' ? null : readConsentRecord();
  const gateRecord = consentKnown ? record : liveRecord;
  const gateGpc = consentKnown ? gpc : readGpc();
  const gateChoice = consentChoiceOf(gateRecord);
  const gateKnown = consentKnown || gateChoice !== 'none';
  const decision = decide({
    country: detectedRegionFromGeoIP,
    province: geoRegion,
    storefrontCountry: storefrontCountry(store),
    choice,
    categories: record?.choice === 'custom' ? record.categories : null,
    savedModes: record?.modes,
    gpc,
    page,
    tripleWhale: true,
  });
  const gateDecision = decide({
    country: detectedRegionFromGeoIP,
    province: geoRegion,
    storefrontCountry: storefrontCountry(store),
    choice: gateChoice,
    categories:
      gateRecord?.choice === 'custom' ? gateRecord.categories : null,
    savedModes: gateRecord?.modes,
    gpc: gateGpc,
    page,
    tripleWhale: true,
  });

  const granted = useMemo<GrantedCategories>(
    () => grantCategories(stack, consentKnown, decision, choice, bannerReady),
    [
      stack,
      consentKnown,
      choice,
      bannerReady,
      decision.modes.stats,
      decision.modes.analytics,
      decision.modes.ads,
      decision.effective.stats,
      decision.effective.analytics,
      decision.effective.ads,
      decision.effective.replay,
      decision.effective.attribution,
    ],
  );
  const gateGranted = useMemo<GrantedCategories>(
    () =>
      grantCategories(stack, gateKnown, gateDecision, gateChoice, bannerReady),
    [
      stack,
      gateKnown,
      gateChoice,
      bannerReady,
      gateDecision.modes.stats,
      gateDecision.modes.analytics,
      gateDecision.modes.ads,
      gateDecision.effective.stats,
      gateDecision.effective.analytics,
      gateDecision.effective.ads,
      gateDecision.effective.replay,
      gateDecision.effective.attribution,
    ],
  );

  // Read the landing URL into memory before any tracker loads (layout effects
  // run in order), then keep click ids out of the URL unless ads are allowed.
  const adsAllowedHere = gateDecision.effective.ads;
  useLayoutEffect(() => {
    rememberLandingParams(window.location.search);
    if (stack === 'next' && page !== 'private') {
      captureCampaignFromLocation({
        search: window.location.search,
        pathname,
        store,
      });
    }
    syncClickIdsInUrl(adsAllowedHere);
  }, [stack, page, pathname, store, adsAllowedHere]);

  // A no (a stored choice, or GPC for ads) keeps that category's vendor
  // cookies gone on every load. Judged off the page tier, so a health page
  // does not wipe consented ad cookies.
  const choiceDecision =
    page === 'marketing' ? decision : decide({
      country: detectedRegionFromGeoIP,
      province: geoRegion,
      storefrontCountry: storefrontCountry(store),
      choice,
      categories: record?.choice === 'custom' ? record.categories : null,
      savedModes: record?.modes,
      gpc,
      tripleWhale: true,
    });
  const adsRefused =
    consentKnown && (choice !== 'none' || gpc) && !choiceDecision.effective.ads;
  const analyticsRefused =
    consentKnown && choice !== 'none' && !choiceDecision.effective.analytics;
  useEffect(() => {
    if (adsRefused || analyticsRefused) {
      expireTrackerStorage({ ads: adsRefused, analytics: analyticsRefused });
    }
  }, [adsRefused, analyticsRefused]);

  useEffect(() => {
    if (stack !== 'next' || !granted.ads) return;
    persistCampaign();
    persistLandingClickIds();
  }, [stack, granted.ads]);

  setConsentGate({ stack: 'next', ...gateGranted });

  const lastTripleWhalePath = useRef<string | null>(null);
  const attributionAllowed = gateDecision.effective.attribution;
  useEffect(() => {
    releaseTripleWhaleProductView(pathname);
    if (!attributionAllowed) return;
    if (lastTripleWhalePath.current === null) {
      lastTripleWhalePath.current = pathname;
      return;
    }
    if (pathname === lastTripleWhalePath.current) return;
    lastTripleWhalePath.current = pathname;
    if (tripleWhaleProductViewCovers(pathname)) return;
    trackTripleWhalePageLoad();
  }, [pathname, attributionAllowed]);

  // Reddit and Meta do not follow in-app navigation on their own (Meta's
  // pushState tracking is off), so route changes are sent here, only where ads are allowed.
  const lastAdsPath = useRef<string | null>(null);
  useEffect(() => {
    if (stack !== 'next' || !granted.ads) return;
    if (lastAdsPath.current === null) {
      lastAdsPath.current = pathname;
      return;
    }
    if (pathname === lastAdsPath.current) return;
    lastAdsPath.current = pathname;
    window.rdt?.('track', 'PageVisit');
    window.fbq?.('track', 'PageView');
  }, [stack, granted.ads, pathname]);

  // Loaded trackers keep watching the URL. A link into a stricter tier gets a
  // full document load instead of an in-app push; anything else that lands
  // there (router.push, back/forward) reloads.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const anchor = (event.target as Element | null)?.closest?.('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if ((anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) {
        return;
      }
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (!needsFreshDocument(classifyRoute(url.pathname, url.search))) return;
      event.preventDefault();
      event.stopPropagation();
      window.location.assign(url.href);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    if (needsFreshDocument(page)) window.location.reload();
  }, [page]);

  // A full load out of a health or private page would otherwise hand its URL
  // to the next page as document.referrer, which pixels read.
  useEffect(() => {
    if (page === 'marketing') return;
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'strict-origin';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [page]);

  useLayoutEffect(() => {
    if (stack !== 'next' || !gateKnown) return;
    if (gateGranted.analytics) {
      loadMixpanel(gateGranted.ads);
    }
    if (gateGranted.replay) loadClarity(gateGranted.ads);
    if (gateGranted.gtm) loadGtm(gateGranted.ads);
    if (page === 'health' && gateGranted.analytics) loadGa4();
    if (gateGranted.ads) {
      loadGoogleAds();
      loadMeta();
      loadTikTok();
      loadReddit();
      loadHubSpot();
      if (!trackerSkipsStorefront('wewomedia', region)) loadWewomedia();
      captureAdClickIds();
    }
    if (gateGranted.attribution) loadTripleWhale(store);
  }, [stack, gateKnown, gateGranted, page, region, store]);

  const choose = useCallback(
    (next: 'accept' | 'reject') => {
      writeConsentRecord({
        v: 1,
        ts: Date.now(),
        store,
        choice: next,
        ...(gpc ? { gpc: true } : {}),
      });
      if (next === 'reject') {
        // Drop queued emits and stored ad ids so pagehide cannot flush them.
        clearAnalyticsQueue();
        forgetCampaignClickIds();
        forgetLandingClickIds();
        forgetTrackerData({ ads: true, analytics: true });
        window.location.reload();
        return;
      }
      setRecord({
        v: 1,
        ts: Date.now(),
        store,
        choice: next,
        ...(gpc ? { gpc: true } : {}),
      });
      setDialogOpen(false);
    },
    [gpc, store],
  );

  const openPrivacyChoices = useCallback(() => {
    setDialogOpen(true);
  }, []);
  const closePrivacyChoices = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const { stats: statsMode, analytics: analyticsMode, ads: adsMode } =
    decision.modes;
  const saveCategories = useCallback(
    (categories: ConsentCategorySelection) => {
      const modes = { stats: statsMode, analytics: analyticsMode, ads: adsMode };
      const nextDecision = decide({
        country: detectedRegionFromGeoIP,
        province: geoRegion,
        storefrontCountry: storefrontCountry(store),
        choice: 'custom',
        categories,
        savedModes: modes,
        gpc,
        page,
        tripleWhale: true,
      });
      const nextGtm = isGtmAllowed({
        analytics: nextDecision.effective.analytics,
        ads: nextDecision.effective.ads,
      });
      writeConsentRecord({
        v: 1,
        ts: Date.now(),
        store,
        choice: 'custom',
        categories,
        modes,
        ...(gpc ? { gpc: true } : {}),
      });
      const downgrade =
        (granted.stats && !nextDecision.effective.stats) ||
        (granted.analytics && !nextDecision.effective.analytics) ||
        (granted.ads && !nextDecision.effective.ads) ||
        (granted.replay && !nextDecision.effective.replay) ||
        (granted.gtm && !nextGtm) ||
        (granted.attribution && !nextDecision.effective.attribution);
      if (downgrade) {
        clearAnalyticsQueue();
        if (granted.ads && !nextDecision.effective.ads) {
          forgetCampaignClickIds();
          forgetLandingClickIds();
        }
        forgetTrackerData({
          ads: granted.ads && !nextDecision.effective.ads,
          analytics: granted.analytics && !nextDecision.effective.analytics,
        });
        window.location.reload();
        return;
      }
      setRecord({
        v: 1,
        ts: Date.now(),
        store,
        choice: 'custom',
        categories,
        modes,
        ...(gpc ? { gpc: true } : {}),
      });
      setDialogOpen(false);
    },
    [
      detectedRegionFromGeoIP,
      geoRegion,
      gpc,
      granted,
      statsMode,
      analyticsMode,
      adsMode,
      page,
      store,
    ],
  );

  const value = useMemo<ConsentRuntimeValue>(
    () => ({
      stack,
      choice,
      consentKnown,
      gpc,
      granted,
      setBannerReady,
      choose,
      openPrivacyChoices,
      saveCategories,
    }),
    [
      stack,
      choice,
      consentKnown,
      gpc,
      granted,
      choose,
      openPrivacyChoices,
      saveCategories,
    ],
  );

  return (
    <ConsentRuntimeContext.Provider value={value}>
      <div
        hidden
        data-consent-known={consentKnown ? 'true' : 'false'}
        data-consent-stats={stack === 'next' && granted.stats ? 'true' : 'false'}
        data-consent-analytics={
          stack === 'next' && granted.analytics ? 'true' : 'false'
        }
        data-consent-ads={stack === 'next' && granted.ads ? 'true' : 'false'}
        data-consent-gtm={stack === 'next' && granted.gtm ? 'true' : 'false'}
      />
      <PrivacyChoicesDialog
        open={dialogOpen}
        gpc={gpc}
        granted={granted}
        onClose={closePrivacyChoices}
        onAccept={() => choose('accept')}
        onDecline={() => choose('reject')}
        onSave={saveCategories}
      />
      {children}
    </ConsentRuntimeContext.Provider>
  );
}
